import React, { useMemo } from "react";
import { Autocomplete, Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { ValueEditor } from "./ValueEditor";

export const OPERATOR_LABELS = {
  is: "is",
  is_not: "is not",
  any_of: "is any of",
  none_of: "is none of",
  contains: "contains",
  before: "is before",
  after: "is after",
  between: "is between",
  is_empty: "is empty",
  is_not_empty: "is not empty",
};

const SOURCE_ORDER = ["final result", "capture", "collection site", "collection method", "device", "contributed", "algorithm"];
const sourceLabel = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "Other");

const newCondition = () => ({ field: null, operator: null, value: null });

// a value carried over to another operator where it still fits
const carryValue = (value, from, to) => {
  const list = ["any_of", "none_of"];
  if (["is_empty", "is_not_empty"].includes(to)) return null;
  if (list.includes(to)) return Array.isArray(value) ? value : value != null && value !== "" ? [value] : [];
  if (list.includes(from)) return Array.isArray(value) && value.length ? value[0] : null;
  if (to === "between" || from === "between") return null;
  return value;
};

const Combinator = ({ value, onChange, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography variant="body2">Match</Typography>
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ "aria-label": label }}
      sx={{ width: 90 }}
    >
      <MenuItem value="and">all</MenuItem>
      <MenuItem value="or">any</MenuItem>
    </TextField>
    <Typography variant="body2">of these conditions</Typography>
  </Box>
);

const Condition = ({ fields, condition, onChange, onRemove, index }) => {
  const field = fields.find((f) => f.key === condition.field) ?? null;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }} data-testid="filter-condition">
      <Autocomplete
        size="small"
        options={fields}
        groupBy={(f) => sourceLabel(f.source)}
        getOptionLabel={(f) => f.label}
        isOptionEqualToValue={(a, b) => a.key === b.key}
        value={field}
        onChange={(_, f) =>
          onChange(f ? { field: f.key, operator: f.filterOps[0], value: null } : newCondition())
        }
        // wider than the field itself: each option carries its description
        slotProps={{ popper: { placement: "bottom-start", style: { width: 400 } } }}
        // named by its label, described by its description
        renderOption={({ key, ...props }, f) => (
          <li key={f.key} {...props} aria-label={f.label} aria-describedby={`field-help-${f.key}`}>
            <Box>
              <Box>{f.label}</Box>
              <Typography
                id={`field-help-${f.key}`}
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.3 }}
              >
                {f.description}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Field" inputProps={{ ...params.inputProps, "aria-label": `Condition ${index + 1} field` }} />}
        sx={{ width: 240 }}
      />
      <TextField
        select
        size="small"
        label="Operator"
        disabled={!field}
        value={condition.operator ?? ""}
        onChange={(e) =>
          onChange({ ...condition, operator: e.target.value, value: carryValue(condition.value, condition.operator, e.target.value) })
        }
        sx={{ width: 150 }}
      >
        {(field?.filterOps ?? []).map((op) => (
          <MenuItem key={op} value={op}>
            {OPERATOR_LABELS[op] ?? op}
          </MenuItem>
        ))}
      </TextField>
      {/* a fresh editor for each field: a picker keeps text typed but never
          chosen, which would otherwise carry over to the next field */}
      <ValueEditor
        key={condition.field ?? ""}
        field={field}
        operator={condition.operator}
        value={condition.value}
        onChange={(value) => onChange({ ...condition, value })}
      />
      <Tooltip title="Remove condition">
        <IconButton aria-label={`Remove condition ${index + 1}`} onClick={onRemove} size="small" sx={{ mt: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const isGroup = (node) => node && Array.isArray(node.clauses);

/**
 * Conditions on any registry field, ANDed or ORed, with groups one level
 * deep: "species is Culex pipiens AND (site is Alpha OR site group is North)".
 */
export const FilterBuilder = ({ fields, value, onChange, depth = 0, onRemove }) => {
  const group = value ?? { op: "and", clauses: [] };
  const sorted = useMemo(
    () =>
      [...fields].sort(
        (a, b) =>
          SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source) || a.label.localeCompare(b.label)
      ),
    [fields]
  );
  const set = (clauses) => onChange({ ...group, clauses });
  const update = (i, next) => set(group.clauses.map((c, j) => (j === i ? next : c)));
  const remove = (i) => set(group.clauses.filter((_, j) => j !== i));

  return (
    <Box
      role="group"
      aria-label={depth ? "Condition group" : "Advanced filters"}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        ...(depth && { borderLeft: 3, borderColor: "primary.light", pl: 2, pr: 1, py: 1, bgcolor: "action.hover", borderRadius: 1 }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Combinator value={group.op} onChange={(op) => onChange({ ...group, op })} label={depth ? "Group match" : "Match"} />
        {depth > 0 && (
          <Button size="small" onClick={onRemove} sx={{ ml: "auto" }}>
            Remove group
          </Button>
        )}
      </Box>
      {group.clauses.map((c, i) =>
        isGroup(c) ? (
          <FilterBuilder
            key={i}
            fields={fields}
            value={c}
            depth={depth + 1}
            onChange={(next) => update(i, next)}
            onRemove={() => remove(i)}
          />
        ) : (
          <Condition key={i} index={i} fields={sorted} condition={c} onChange={(next) => update(i, next)} onRemove={() => remove(i)} />
        )
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => set([...group.clauses, newCondition()])}>
          Add condition
        </Button>
        {depth === 0 && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => set([...group.clauses, { op: "or", clauses: [newCondition()] }])}>
            Add group
          </Button>
        )}
      </Box>
    </Box>
  );
};
